import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { ChatFlow } from '../chat-bot/types/chat-flow.interface';
import { CommonModule } from '@angular/common';
import { IconRegistryService } from 'src/app/shared/services/icon-registry.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { CustomVariablesService } from '../chat-bot/components/ui-chat-bot-sa/custom-variables.service';
import { FontLoaderService } from 'src/app/shared/services/font-loader.service';
import { Subject, takeUntil, TimeoutError } from 'rxjs';
import { ChatBotFlowSaService } from '../chat-bot/components/ui-chat-bot-sa/chat-bot-flow-sa.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalRoutes } from 'src/app/shared/utils/route-helper';

@Component({
  selector: 'load-chatbot',
  templateUrl: './load-chatbot.component.html',
  styleUrls: ['./load-chatbot.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule],
  providers: [],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LoadChatbot implements OnInit {
  private destroy$ = new Subject<void>();
  errorFunnelNotPublished = false;
  errorFunnelNotfound = false;
  errorFunnelStatus: 'funnel_not_found' | 'funnel_not_published' | undefined;
  chatFlow: ChatFlow | null = null;
  funnelLanguage: string | undefined;
  testMode: boolean = false;
  constructor(
    private chatBotFlowSaService: ChatBotFlowSaService,
    private activatedRoute: ActivatedRoute,
    private iconRegistry: IconRegistryService,
    private customVariablesService: CustomVariablesService,
    private fontLoader: FontLoaderService,
    private translateService: TranslateService
  ) {}

  /**
   * Direct asset path for the error illustration.
   *
   * Deliberately not a `mat-icon`/`MatIconRegistry` icon: this page only renders
   * *because* a request already failed, so the connection the registry would
   * need to fetch the icon over is usually already gone
   */
  get errorIconUrl(): string {
    const icon =
      this.errorFunnelStatus === 'funnel_not_published'
        ? 'construction-emoji'
        : 'fog-emoji';
    return `${LocalRoutes.chat_bot_icons}${icon}.svg`;
  }

  ngOnInit(): void {
    this.iconRegistry.initialize();
    let subdomain: string;
    let funnel_selector: string;
    let lang: string;
    this.activatedRoute.params.subscribe((params) => {
      // Assuming the URL is structured as '/subdomain/funnelSelector'
      subdomain = params['subdomain'];
      funnel_selector = params['funnelSelector'] ?? '';
      lang = params['lang'];
    });
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      if (lang) {
        this.funnelLanguage = lang;
      }
      const resumeFeedbackId = this.chatBotFlowSaService.getResumableFeedbackId(
        subdomain,
        funnel_selector,
        queryParams['touchpoint']
      );
      this.chatBotFlowSaService
        .fetchFlowConfigFromApi(
          subdomain,
          funnel_selector,
          queryParams,
          lang,
          resumeFeedbackId
        )
        .subscribe({
          next: (data) => {
            this.chatFlow = data;
            console.log('Fetched chat flow configuration:', this.chatFlow);

            // Assign query parameters to Variables if keys match, excluding "test"
            Object.keys(queryParams).forEach((key) => {
              if (
                key !== 'test' &&
                this.chatFlow &&
                this.chatFlow.variables.hasOwnProperty(key)
              ) {
                this.chatFlow.variables[key] = queryParams[key];
              }
            });
            // Set the custom variables in customVariables service
            this.customVariablesService.setCustomVariables(
              this.chatFlow.variables
            );
            if (!this.funnelLanguage) {
              this.funnelLanguage = data.language.current_language;
            }

            // Set the custom font if available
            if (this.chatFlow?.design.typography.custom_font_url) {
              this.fontLoader
                .loadCustomFont(
                  this.chatFlow?.design.typography.custom_font_url,
                  'CustomChatBotFont'
                )
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (lol) => {},
                  error: () => {
                    console.warn(
                      'Custom font could not be loaded. Using fallback font'
                    );
                  }
                });
            }
          },
          error: (error) => {
            this.translateService.use(lang || 'en');
            if (error?.error?.detail?.error == 'funnel_not_published') {
              this.errorFunnelStatus = 'funnel_not_published';
            } else {
              this.errorFunnelStatus = 'funnel_not_found';
            }
            this.reportFlowConfigFailure(error, {
              subdomain,
              funnelSelector: funnel_selector,
              touchpoint: queryParams['touchpoint'],
              lang
            });
          }
        });
    });
  }

  /**
   * Report a chatflow config failure the user had no hand in.
   *
   * Only unexpected failures are captured. A 4xx is a verdict about the URL — an
   * expired link, a funnel that isn't published — which is a normal outcome
   * rather than a defect, and reporting it would bury the real signal.
   */
  private reportFlowConfigFailure(
    error: unknown,
    context: {
      subdomain?: string;
      funnelSelector?: string;
      touchpoint?: string;
      lang?: string;
    }
  ): void {
    const status =
      error instanceof HttpErrorResponse ? error.status : undefined;
    let kind: string;
    if (error instanceof TimeoutError) {
      kind = 'timeout';
    } else if (status === 0) {
      kind = 'network';
    } else if (status !== undefined && status >= 500) {
      kind = 'server';
    } else {
      return;
    }

    // A stable per-kind message so Sentry groups these three causes separately —
    // a dropped connection and a failing backend need different owners.
    Sentry.captureException(
      new Error(`Chatflow config request failed (${kind})`),
      {
        tags: {
          failure_kind: kind,
          http_status: status ?? 'none',
          subdomain: context.subdomain || 'unknown',
          funnel_selector: context.funnelSelector || 'none',
          touchpoint: context.touchpoint || 'none',
          lang: context.lang || 'unknown'
        }
      }
    );
  }
}
